/**
 * 1.8.4 (D-1.8.4-6) — Past events, with reports.
 *
 * One collapsed section, newest first. At its top, **Generate report — last 12 months**: every past event of the twelve
 * months in a row, the totals, and the two attendance charts ("Events in charts", D-1.7.5-1, moved here from the page).
 * Each past event: its day, place and kind, Open (its page — the lists are there), and **Generate report**: one page
 * with the date, the place, the kind and the numbers that applied. Each report has Print (the browser's print on ABF
 * letterhead — PrintArea, the pattern of the other printables), PDF (made by the server on letterhead, the same
 * numbers) and Copy (plain text). Counts only, never a name: the numbers come from `shared/event-report.ts` over the
 * events list the page already holds; no RSVP line is read.
 *
 * Raffle History (1.7.4) is its own drop-down inside the section: the draw's evidence, complete, as it was.
 */
import { Fragment, useCallback, useId, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { keyToNoonUtc } from '@shared/dates';
import { eventsListCharts, type EventsListWords } from '@shared/event-charts';
import { REPORT_COUNTS, eventReport, eventReportText, reportDay, reportTime, yearReport, yearReportText, type EventReport, type ReportLang, type YearReport } from '@shared/event-report';
import type { EventView, EventsResponse } from '@shared/events';
import { staffName } from '@shared/people';
import { useAnnounce } from '@/components/Announcer';
import { Chart } from '@/components/Chart';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { formatDayKey } from '@/lib/dates';
import { PrintArea } from '@/lib/docs';
import { errorText } from '@/features/people/api';
import { reportPdfReq } from './api184';
import { saveBase64 } from './page175/save';
import '@/styles/ev184.css';

type T = (k: string, o?: Record<string, unknown>) => string;
const eid = (id: string) => encodeURIComponent(id);

/** "Tech discussion · On the calendar only", in the screen's words. */
const kindWords = (r: Pick<EventReport, 'kind' | 'shape'>, t: T) => [r.kind, t(`ev184.report.shape.${r.shape}`)].filter(Boolean).join(' · ');
function raffleLine(r: EventReport, t: T): string {
  const x = r.raffle!;
  return x.drawn ? t('ev184.report.raffleDrawn', { places: x.places ?? t('ev184.report.noLimit'), guaranteed: x.guaranteed, winners: x.winners, pool: x.pool, waiting: x.waiting }) : t('ev184.report.raffleNotDrawn');
}
const volunteersLine = (r: EventReport, t: T) => (!r.volunteers ? '—' : r.volunteers.needed === null ? String(r.volunteers.going) : t('ev184.report.volunteersOf', { going: r.volunteers.going, needed: r.volunteers.needed }));

/** Print · PDF · Copy, and what the press did (said aloud too). */
function ReportActions({ ids, onPrint, pdfBody, text, canPdf }: { ids: { print: string; pdf: string; copy: string; said: string }; onPrint: () => void; pdfBody: Parameters<typeof reportPdfReq>[0]; text: () => string; canPdf: boolean }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ text: string; bad: boolean } | null>(null);
  const say = (m: string, bad = false) => {
    setSaid({ text: m, bad });
    announce(m, bad ? 'assertive' : 'polite');
  };
  const pdf = async () => {
    setBusy(true);
    try {
      const r = await reportPdfReq(pdfBody);
      saveBase64(r.name, r.base64, 'application/pdf');
      say(t('ev184.report.saved', { name: r.name }));
    } catch (x) {
      say(errorText(x), true);
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text());
      say(t('ev184.report.copied'));
    } catch {
      say(t('ev184.report.copyFailed'), true);
    }
  };
  return (
    <>
      <div className="chips no-print" role="group" aria-label={t('ev184.report.actions')}>
        <Button size="sm" onClick={onPrint} data-testid={ids.print}>
          <Icon name="printer" /> {t('ev184.report.print')}
        </Button>
        {canPdf ? (
          <Button variant="secondary" size="sm" disabled={busy} aria-busy={busy || undefined} onClick={() => pdf()} data-testid={ids.pdf}>
            <Icon name="download" /> {t('ev184.report.pdf')}
          </Button>
        ) : null}
        <Button variant="secondary" size="sm" onClick={() => copy()} data-testid={ids.copy}>
          <Icon name="copy" /> {t('ev184.report.copy')}
        </Button>
      </div>
      {said ? (
        <p className={said.bad ? 'field-err' : 'help'} data-testid={ids.said}>
          {said.text}
        </p>
      ) : null}
    </>
  );
}

/** The body of one event's report — on screen and on paper alike. */
function EventReportBody({ r, lang, Heading = 'h3', headingId, paper = false }: { r: EventReport; lang: ReportLang; Heading?: 'h3' | 'h4' | 'h1'; headingId?: string; paper?: boolean }) {
  const { t } = useTranslation();
  const time = reportTime(r);
  const row = (k: string, label: string, value: ReactNode) => (
    <Fragment key={k}>
      <dt>{label}</dt>
      <dd className="tnum" data-testid={paper ? undefined : `report-${k}-${r.eventId}`}>
        {value}
      </dd>
    </Fragment>
  );
  return (
    <>
      <Heading id={headingId}>{t('ev184.report.titleOf', { name: r.name })}</Heading>
      <dl className="kv">
        {row('date', t('ev184.report.date'), reportDay(r.dayKey, lang))}
        {time ? row('time', t('ev184.report.time'), time) : null}
        {row('place', t('ev184.report.place'), r.place || '—')}
        {row('kind', t('ev184.report.kind'), kindWords(r, t))}
        {r.counts ? REPORT_COUNTS.map((k) => row(k, t(`ev184.report.${k}`), r.counts![k])) : null}
        {r.volunteers ? row('volunteers', t('ev184.report.volunteers'), volunteersLine(r, t)) : null}
        {r.raffle ? row('raffle', t('ev184.report.raffle'), raffleLine(r, t)) : null}
      </dl>
      {r.cancelled ? <p className="ev184-note">{t('ev184.report.cancelled')}</p> : null}
      {!r.counts ? <p className="ev184-note">{t('ev184.report.calendarOnly')}</p> : null}
      {r.volunteers ? <p className="ev184-note">{t('ev184.report.hoursNotKept')}</p> : null}
      <p className="ev184-note">{t('ev184.report.countsOnly')}</p>
    </>
  );
}

/** One event's report, opened under its row. */
function EventReportPanel({ ev, lang, canPdf }: { ev: EventView; lang: ReportLang; canPdf: boolean }) {
  const r = useMemo(() => eventReport(ev), [ev]);
  const [printing, setPrinting] = useState(false);
  const done = useCallback(() => setPrinting(false), []);
  return (
    <div className="ev184-report" role="region" aria-labelledby={`report-h-${ev.eventId}`} id={`report-panel-${ev.eventId}`} data-testid={`report-panel-${ev.eventId}`}>
      <EventReportBody r={r} lang={lang} Heading="h4" headingId={`report-h-${ev.eventId}`} />
      <ReportActions ids={{ print: `report-print-${ev.eventId}`, pdf: `report-pdf-${ev.eventId}`, copy: `report-copy-${ev.eventId}`, said: `report-said-${ev.eventId}` }} onPrint={() => setPrinting(true)} pdfBody={{ scope: 'event', eventId: ev.eventId, lang }} text={() => eventReportText(r, lang)} canPdf={canPdf} />
      <PrintArea active={printing} onDone={done}>
        {printing ? <EventReportBody r={r} lang={lang} Heading="h1" paper /> : null}
      </PrintArea>
    </div>
  );
}

/** The year's table: every past event in a row, the numbers that applied ("—" where none did). */
function YearTable({ y, lang, testId }: { y: YearReport; lang: ReportLang; testId?: string }) {
  const { t } = useTranslation();
  if (!y.rows.length) return <p className="help">{t('ev184.report.none')}</p>;
  return (
    <div className="table-wrap" tabIndex={0} role="region" aria-label={t('ev184.report.tableCaption')}>
      <table className="table" data-testid={testId}>
        <caption className="sr-only">{t('ev184.report.tableCaption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('ev184.report.date')}</th>
            <th scope="col">{t('ev184.report.event')}</th>
            {REPORT_COUNTS.map((k) => (
              <th key={k} scope="col">
                {t(`ev184.report.${k}`)}
              </th>
            ))}
            <th scope="col">{t('ev184.report.volunteers')}</th>
          </tr>
        </thead>
        <tbody>
          {y.rows.map((r) => (
            <tr key={r.eventId} data-event={r.eventId}>
              <td>{formatDayKey(r.dayKey, lang)}</td>
              <th scope="row">
                {r.name}
                <br />
                <span className="help">{[kindWords(r, t), r.cancelled ? t('ev184.cards.cancelled') : '', r.raffle ? raffleLine(r, t) : ''].filter(Boolean).join(' · ')}</span>
              </th>
              {REPORT_COUNTS.map((k) => (
                <td key={k} className="tnum">
                  {r.counts && !r.cancelled ? r.counts[k] : '—'}
                </td>
              ))}
              <td className="tnum">{r.cancelled ? '—' : volunteersLine(r, t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const TOTAL_KEYS = ['events', 'eventsCancelled', 'invited', 'saidYes', 'came', 'households', 'guests', 'volunteers'] as const;
const totalOf = (y: YearReport, k: (typeof TOTAL_KEYS)[number]) => (k === 'eventsCancelled' ? y.totals.cancelled : y.totals[k]);

/** The two attendance charts (1.7.5 "Events in charts"), now inside the 12-month report. */
function EventsInCharts({ events, todayKey, lang }: { events: EventView[]; todayKey: string; lang: ReportLang }) {
  const { t } = useTranslation();
  const month = (dayKey: string) => new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(keyToNoonUtc(dayKey)));
  const [byEvent, year] = eventsListCharts(events, todayKey, t('num175.events', { returnObjects: true }) as unknown as EventsListWords, month);
  return (
    <section aria-labelledby="h-ev-charts" data-testid="events-charts">
      <h4 id="h-ev-charts">{t('num175.eventsCard.title')}</h4>
      <div className="num175-pair">
        <Chart spec={byEvent!} testId="chart-by-event" />
        <Chart spec={year!} testId="chart-year" />
      </div>
    </section>
  );
}

function YearReportBody({ y, lang, events, todayKey, Heading = 'h3', headingId }: { y: YearReport; lang: ReportLang; events: EventView[]; todayKey: string; Heading?: 'h3' | 'h1'; headingId?: string }) {
  const { t } = useTranslation();
  return (
    <>
      <Heading id={headingId}>{t('ev184.report.yearTitle')}</Heading>
      <p className="help">{t('ev184.report.window', { from: reportDay(y.fromKey, lang), to: reportDay(y.toKey, lang) })}</p>
      <h4>{t('ev184.report.totals')}</h4>
      <dl className="kv">
        {TOTAL_KEYS.map((k) => (
          <Fragment key={k}>
            <dt>{t(`ev184.report.${k}`)}</dt>
            <dd className="tnum" data-testid={Heading === 'h1' ? undefined : `report-year-total-${k}`}>
              {totalOf(y, k)}
            </dd>
          </Fragment>
        ))}
      </dl>
      <p className="ev184-note">{t('ev184.report.hoursNotKept')}</p>
      <YearTable y={y} lang={lang} {...(Heading === 'h1' ? {} : { testId: 'report-year-table' })} />
      {Heading === 'h1' ? null : <EventsInCharts events={events} todayKey={todayKey} lang={lang} />}
      <p className="ev184-note">{t('ev184.report.countsOnly')}</p>
    </>
  );
}

function YearReportPanel({ events, todayKey, lang, canPdf }: { events: EventView[]; todayKey: string; lang: ReportLang; canPdf: boolean }) {
  const y = useMemo(() => yearReport(events, todayKey), [events, todayKey]);
  const [printing, setPrinting] = useState(false);
  const done = useCallback(() => setPrinting(false), []);
  return (
    <div className="ev184-report" role="region" aria-labelledby="report-year-h" id="report-year-panel" data-testid="report-year-panel">
      <YearReportBody y={y} lang={lang} events={events} todayKey={todayKey} headingId="report-year-h" />
      <ReportActions ids={{ print: 'report-year-print', pdf: 'report-year-pdf', copy: 'report-year-copy', said: 'report-year-said' }} onPrint={() => setPrinting(true)} pdfBody={{ scope: 'year', lang }} text={() => yearReportText(y, lang)} canPdf={canPdf} />
      <PrintArea active={printing} onDone={done}>
        {printing ? (
          <>
            <YearReportBody y={y} lang={lang} events={events} todayKey={todayKey} Heading="h1" />
            <EventsInChartsPrint events={events} todayKey={todayKey} lang={lang} />
          </>
        ) : null}
      </PrintArea>
    </div>
  );
}

/** The charts on paper (their own test ids are the screen's). */
function EventsInChartsPrint({ events, todayKey, lang }: { events: EventView[]; todayKey: string; lang: ReportLang }) {
  const { t } = useTranslation();
  const month = (dayKey: string) => new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(keyToNoonUtc(dayKey)));
  const [byEvent, year] = eventsListCharts(events, todayKey, t('num175.events', { returnObjects: true }) as unknown as EventsListWords, month);
  return (
    <>
      <h2>{t('num175.eventsCard.title')}</h2>
      <Chart spec={byEvent!} testId="print-chart-by-event" />
      <Chart spec={year!} testId="print-chart-year" />
    </>
  );
}

/** Raffle History (1.7.4): every draw — the evidence that is kept. Unchanged, now inside Past events. */
function RaffleHistory({ history, lang }: { history: EventsResponse['history']; lang: string }) {
  const { t } = useTranslation();
  const day = (iso: string) => new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' }).format(new Date(iso));
  return (
    <section aria-labelledby="h-rh" data-testid="raffle-history">
      <h3 id="h-rh" className="sr-only">
        {t('events.history.title')}
      </h3>
      <p className="help" data-testid="raffle-history-how">
        {t('events.history.caption')}
      </p>
      {history.length ? (
        <div className="table-wrap" tabIndex={0} role="region" aria-label={t('events.history.caption')}>
          <table className="table">
            <caption className="sr-only">{t('events.history.caption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('events.history.draw')}</th>
                <th scope="col">{t('events.history.event')}</th>
                <th scope="col">{t('ev174.history.kind')}</th>
                <th scope="col">{t('ev174.history.places')}</th>
                <th scope="col">{t('ev174.history.pool')}</th>
                <th scope="col">{t('ev174.history.winners')}</th>
                <th scope="col">{t('ev174.history.fingerprint')}</th>
                <th scope="col">{t('events.history.who')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, k) => (
                <tr key={`${h.eventId}-${k}`} data-testid={`history-${h.eventId}-${k}`}>
                  <td className="tnum">{day(h.drawDateTime)}</td>
                  <td>
                    {h.eventName} · {formatDayKey(h.eventDayKey, lang)}
                  </td>
                  <td>
                    {h.kind === 'volunteers' ? t('ev174.history.volunteers') : t('ev174.history.tenants')}
                    {h.again ? ` · ${t('ev174.history.again')}` : ''}
                  </td>
                  <td className="tnum">{h.places ?? t('ev174.history.noLimit')}</td>
                  <td className="tnum">{h.poolSize}</td>
                  <td className="tnum">{(h.winners ?? []).length}</td>
                  <td>
                    {h.fingerprint ? (
                      <code className="ev174-fp" title={t('ev174.history.fingerprintFull', { value: h.fingerprint })} data-testid={`history-fp-${h.eventId}-${k}`}>
                        {h.fingerprint.slice(0, 12)}
                      </code>
                    ) : (
                      <span title={t('ev174.history.noFingerprint')}>{t('ev174.history.none')}</span>
                    )}
                  </td>
                  <td>{staffName(h.runBy)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="help">{t('events.history.none')}</p>
      )}
    </section>
  );
}

export interface PastEvents184Props {
  /** The past events (any order: shown newest first). */
  past: EventView[];
  /** Every event the page holds (the 12-month report and its charts read them). */
  all: EventView[];
  todayKey: string;
  history: EventsResponse['history'];
  /** The office (administrators and co-administrators): the PDF is made by the server. */
  canPdf: boolean;
}

export function PastEvents184({ past, all, todayKey, history, canPdf }: PastEvents184Props) {
  const { t, i18n } = useTranslation();
  const lang: ReportLang = i18n.language === 'es' ? 'es' : 'en';
  const uid = useId();
  const [open, setOpen] = useState<string | null>(null);
  const [yearOpen, setYearOpen] = useState(false);
  const rows = useMemo(() => [...past].sort((a, b) => b.dayKey.localeCompare(a.dayKey) || (b.minutes ?? 0) - (a.minutes ?? 0)), [past]);
  return (
    <Card as="section" className="ev184-past" aria-labelledby="h-ev-past" data-testid="events-past">
      <details data-testid="events-past-details">
        <summary data-testid="events-past-summary">
          <h2 id="h-ev-past">{t('events.pastN', { count: past.length })}</h2>
        </summary>
        <div className="ev184-pastin">
          <p className="help">{t('ev184.past.help')}</p>
          <div>
            <Button variant="secondary" aria-expanded={yearOpen} aria-controls="report-year-panel" onClick={() => setYearOpen(!yearOpen)} data-testid="report-year">
              <Icon name="file" /> {yearOpen ? t('ev184.past.yearClose') : t('ev184.past.year')}
            </Button>
          </div>
          {yearOpen ? <YearReportPanel events={all} todayKey={todayKey} lang={lang} canPdf={canPdf} /> : null}
          {!rows.length ? (
            <p className="help calm" data-testid="events-past-none">
              {t('events.pastNone')}
            </p>
          ) : (
            <ul className="plain-list ev184-pastlist" aria-label={t('ev184.past.list')} data-testid="events-past-list">
              {rows.map((e) => {
                const r = eventReport(e);
                const mine = open === e.eventId;
                return (
                  <li key={e.eventId} className="ev184-pastrow" data-past={e.eventId} data-testid={`past-${e.eventId}`}>
                    <b id={`${uid}-${e.eventId}`}>{e.name}</b>
                    <span className="help">{[formatDayKey(e.dayKey, lang), e.place, kindWords(r, t), r.cancelled ? t('ev184.cards.cancelled') : ''].filter(Boolean).join(' · ')}</span>
                    <div className="chips">
                      <Link className="btn secondary sm" to={`/events/${eid(e.eventId)}`} aria-describedby={`${uid}-${e.eventId}`} data-testid={`past-open-${e.eventId}`}>
                        {t('ev184.past.open')}
                      </Link>
                      <Button variant="secondary" size="sm" aria-expanded={mine} aria-controls={`report-panel-${e.eventId}`} aria-describedby={`${uid}-${e.eventId}`} onClick={() => setOpen(mine ? null : e.eventId)} data-testid={`report-${e.eventId}`}>
                        {mine ? t('ev184.past.close') : t('ev184.past.report')}
                      </Button>
                    </div>
                    {mine ? <EventReportPanel ev={e} lang={lang} canPdf={canPdf} /> : null}
                  </li>
                );
              })}
            </ul>
          )}
          <details className="ev184-raffle" data-testid="raffle-history-details">
            <summary data-testid="raffle-history-summary">{t('ev184.past.raffleHistory', { count: history.length })}</summary>
            <RaffleHistory history={history} lang={lang} />
          </details>
        </div>
      </details>
    </Card>
  );
}
